import { Box, Button, Drawer, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ConeIcon from './ConeIcon.tsx';
import TextFieldsRoundedIcon from '@mui/icons-material/TextFieldsRounded';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import CropSquareRoundedIcon from '@mui/icons-material/CropSquareRounded';
import PlayAnimationLinesOrder from './PlayAnimationLinesOrder.tsx';
import { Line, Player } from './DialogPlayBuilder.tsx';

interface PlayBuilderBottomBarProps {
    handleAddOffensivePlayer: (num: number) => void;
    handleAddDefender: (num: number) => void;
    handleAddSpecialDefender: (num: number) => void;
    handleAddCone: (color: string) => void;
    handleAddTextBox: () => void;
    handleAddShadeCircle: () => void;
    handleAddShadeRect: () => void;
    lines: Line[];
    players: Player[];
    onUpdateLines: (lines: Line[]) => void;
    nativeApp?: boolean;
}

export default function PlayBuilderBottomBar({
    handleAddOffensivePlayer,
    handleAddDefender,
    handleAddSpecialDefender,
    handleAddCone,
    handleAddTextBox,
    handleAddShadeCircle,
    handleAddShadeRect,
    lines,
    players,
    onUpdateLines,
    nativeApp,
}: PlayBuilderBottomBarProps) {
    const { t } = useTranslation();
    const isNativeApp = nativeApp ?? false;
    const [showAddElements, setShowAddElements] = useState(false);
    const [showAnimation, setShowAnimation] = useState(false);

    const handleAddAndClose = (action: () => void) => {
        action();
        setShowAddElements(false);
    };

    const DEFAULT_CONE_COLOR = '#000000';

    return (
        <>
            {/* Bottom App Bar */}
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    bgcolor: 'white',
                    borderTop: '1px solid rgba(0,0,0,0.12)',
                    p: 1,
                    px: 1.5,
                    // pb: 3,
                    display: 'flex',
                    gap: 1,
                    zIndex: 1200,
                    boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
                    alignItems: 'stretch',
                }}
            >
                <Button
                    variant="contained"
                    onClick={() => setShowAddElements(true)}
                    sx={{
                        flex: 1,
                        bgcolor: '#546e7a',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '13px',
                        textTransform: 'none',
                        py: 1.25,
                        borderRadius: '8px',
                        minHeight: 0,
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#455a64',
                            boxShadow: 'none',
                        }
                    }}
                    startIcon={<AddRoundedIcon sx={{ fontSize: '20px' }} />}
                >
                    {t('PlayBuilderBottomBar00')}
                </Button>
                <Button
                    variant="contained"
                    onClick={() => setShowAnimation(true)}
                    sx={{
                        flex: 1,
                        bgcolor: '#cddc39',
                        color: '#333',
                        fontWeight: 'bold',
                        fontSize: '13px',
                        textTransform: 'none',
                        py: 1.25,
                        borderRadius: '8px',
                        minHeight: 0,
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: '#c0ca33',
                            boxShadow: 'none',
                        }
                    }}
                    startIcon={<TimelineRoundedIcon sx={{ fontSize: '20px' }} />}
                >
                    {t('PlayBuilderBottomBar01')}
                </Button>
            </Box>

            {/* Add Elements Drawer */}
            <Drawer
                anchor="bottom"
                open={showAddElements}
                onClose={() => setShowAddElements(false)}
                sx={{ zIndex: 1400 }}
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: '10px',
                        borderTopRightRadius: '10px',
                        maxHeight: '55vh',
                        backgroundColor: 'white',
                    }
                }}
            >
                <Box sx={{ p: 1.5 }}>
                    {/* Handle bar */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
                        <Box sx={{ width: 32, height: 3, bgcolor: '#e0e0e0', borderRadius: 2 }} />
                    </Box>

                    {/* Offensive Players */}
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#00398f', mb: 0.75, textAlign: 'center' }}>
                        {t('AddElementsPopup00')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5, justifyContent: 'center' }}>
                        {[...Array(6)].map((_, i) => i + 1).map((num) => (
                            <Button
                                key={num}
                                variant="outlined"
                                onClick={() => handleAddAndClose(() => handleAddOffensivePlayer(num))}
                                sx={{
                                    width: 38,
                                    height: 38,
                                    minWidth: 0,
                                    p: 0,
                                    borderRadius: 1.5,
                                    bgcolor: 'white',
                                    color: '#00398f',
                                    borderColor: '#e0e0e0',
                                    borderWidth: 1.5,
                                    '&:hover': {
                                        bgcolor: '#f0f4ff',
                                        borderColor: '#00398f',
                                        transform: 'scale(1.05)',
                                    },
                                    fontSize: 15,
                                    fontWeight: 'bold',
                                }}
                            >
                                {num}
                            </Button>
                        ))}
                        <Button
                            variant="outlined"
                            onClick={() => handleAddAndClose(() => handleAddOffensivePlayer(0))}
                            sx={{
                                width: 38,
                                height: 38,
                                minWidth: 0,
                                p: 0,
                                borderRadius: 1.5,
                                bgcolor: 'white',
                                color: '#00398f',
                                borderColor: '#e0e0e0',
                                borderWidth: 1.5,
                                '&:hover': {
                                    bgcolor: '#f0f4ff',
                                    borderColor: '#00398f',
                                    transform: 'scale(1.05)',
                                },
                                fontSize: 15,
                                fontWeight: 'bold',
                            }}
                        >
                            C
                        </Button>
                    </Box>

                    {/* Defenders */}
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: 'red', mb: 0.75, textAlign: 'center' }}>
                        {t('AddElementsPopup01')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5, justifyContent: 'center' }}>
                        {[...Array(6)].map((_, i) => i + 1).map((num) => (
                            <Button
                                key={num}
                                variant="outlined"
                                onClick={() => handleAddAndClose(() => handleAddDefender(num))}
                                sx={{
                                    width: 38,
                                    height: 38,
                                    minWidth: 0,
                                    p: 0,
                                    borderRadius: 1.5,
                                    bgcolor: 'white',
                                    color: 'red',
                                    borderColor: '#e0e0e0',
                                    borderWidth: 1.5,
                                    '&:hover': {
                                        bgcolor: '#fff0f0',
                                        borderColor: 'red',
                                        transform: 'scale(1.05)',
                                    },
                                    fontSize: 15,
                                    fontWeight: 'bold',
                                }}
                            >
                                {`x${num}`}
                            </Button>
                        ))}
                    </Box>

                    {/* Special Defenders */}
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5, justifyContent: 'center' }}>
                        {[...Array(6)].map((_, i) => i + 1).map((num) => (
                            <Button
                                key={`special-${num}`}
                                variant="outlined"
                                onClick={() => handleAddAndClose(() => handleAddSpecialDefender(num))}
                                sx={{
                                    width: 38,
                                    height: 38,
                                    minWidth: 0,
                                    p: 0,
                                    borderRadius: 1.5,
                                    bgcolor: 'white',
                                    borderColor: '#e0e0e0',
                                    borderWidth: 1.5,
                                    position: 'relative',
                                    overflow: 'visible',
                                    '&:hover': {
                                        bgcolor: '#f8f8f8',
                                        borderColor: '#d32f2f',
                                        transform: 'scale(1.05)',
                                    },
                                }}
                            >
                                <svg
                                    width="38"
                                    height="38"
                                    viewBox="0 0 28 28"
                                    style={{ display: 'block' }}
                                >
                                    {/* Círculo rojo con fondo */}
                                    <circle
                                        cx="14"
                                        cy="14"
                                        r="7.5"
                                        fill="#d32f2f"
                                        stroke="#d32f2f"
                                        strokeWidth="1"
                                    />
                                    {/* Brazo izquierdo - semicírculo pequeño */}
                                    <g transform="translate(5.5, 14) rotate(-30)">
                                        <path
                                            d="M -4 1 A 4 1.2 0 0 1 4 1"
                                            fill="none"
                                            stroke="#d32f2f"
                                            strokeWidth="1.1"
                                            strokeLinecap="round"
                                        />
                                    </g>
                                    {/* Brazo derecho - semicírculo pequeño */}
                                    <g transform="translate(22.5, 14) rotate(30)">
                                        <path
                                            d="M -4 1 A 4 1.2 0 0 1 4 1"
                                            fill="none"
                                            stroke="#d32f2f"
                                            strokeWidth="1.1"
                                            strokeLinecap="round"
                                        />
                                    </g>
                                    {/* Número en blanco - centrado en el círculo */}
                                    <text
                                        x="14"
                                        y="14"
                                        textAnchor="middle"
                                        alignmentBaseline="central"
                                        fontSize="10"
                                        fontWeight="bold"
                                        fill="white"
                                        style={{ fontFamily: 'Arial, sans-serif' }}
                                    >
                                        {num}
                                    </text>
                                </svg>
                            </Button>
                        ))}
                    </Box>

                    {/* Cone, Text, and Shades - all in one row */}
                    <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#000000', mb: 0.75, textAlign: 'center' }}>
                        {t('PlayBuilder17')}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, paddingBottom: isNativeApp ? 5 : 2 }}>
                        {/* Cone - first in line */}
                        <Button
                            variant="outlined"
                            onClick={() => handleAddAndClose(() => handleAddCone(DEFAULT_CONE_COLOR))}
                            sx={{
                                width: 44,
                                height: 44,
                                minWidth: 0,
                                p: 0,
                                borderRadius: 2,
                                bgcolor: 'white',
                                borderColor: '#e0e0e0',
                                borderWidth: 1.5,
                                '&:hover': {
                                    bgcolor: '#f8f8f8',
                                    borderColor: '#546e7a',
                                    transform: 'scale(1.03)',
                                },
                            }}
                            aria-label={t('AddElementsPopup02')}
                            title={t('AddElementsPopup02')}
                        >
                            <ConeIcon size={22} color={DEFAULT_CONE_COLOR} />
                        </Button>
                        {/* Text */}
                        <Button
                            variant="outlined"
                            onClick={() => handleAddAndClose(() => handleAddTextBox())}
                            sx={{
                                width: 44,
                                height: 44,
                                minWidth: 0,
                                p: 0,
                                borderRadius: 2,
                                bgcolor: 'white',
                                borderColor: '#e0e0e0',
                                borderWidth: 1.5,
                                '&:hover': {
                                    bgcolor: '#f8f8f8',
                                    borderColor: '#546e7a',
                                    transform: 'scale(1.03)',
                                },
                            }}
                            aria-label={t('PlayBuilderText01')}
                            title={t('PlayBuilderText01')}
                        >
                            <TextFieldsRoundedIcon sx={{ fontSize: 22, color: '#546e7a' }} />
                        </Button>
                        {/* Shade Circle */}
                        <Button
                            variant="outlined"
                            onClick={() => handleAddAndClose(() => handleAddShadeCircle())}
                            sx={{
                                width: 44,
                                height: 44,
                                minWidth: 0,
                                p: 0,
                                borderRadius: 2,
                                bgcolor: 'white',
                                borderColor: '#e0e0e0',
                                borderWidth: 1.5,
                                '&:hover': {
                                    bgcolor: '#f8f8f8',
                                    borderColor: '#546e7a',
                                    transform: 'scale(1.03)',
                                },
                            }}
                            aria-label={t('AddElementsPopup03')}
                            title={t('AddElementsPopup03')}
                        >
                            <CircleOutlinedIcon sx={{ fontSize: 22, color: '#546e7a' }} />
                        </Button>
                        {/* Shade Rectangle */}
                        <Button
                            variant="outlined"
                            onClick={() => handleAddAndClose(() => handleAddShadeRect())}
                            sx={{
                                width: 44,
                                height: 44,
                                minWidth: 0,
                                p: 0,
                                borderRadius: 2,
                                bgcolor: 'white',
                                borderColor: '#e0e0e0',
                                borderWidth: 1.5,
                                '&:hover': {
                                    bgcolor: '#f8f8f8',
                                    borderColor: '#546e7a',
                                    transform: 'scale(1.03)',
                                },
                            }}
                            aria-label={t('AddElementsPopup04')}
                            title={t('AddElementsPopup04')}
                        >
                            <CropSquareRoundedIcon sx={{ fontSize: 22, color: '#546e7a' }} />
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            {/* Animation Drawer */}
            <Drawer
                anchor="bottom"
                open={showAnimation}
                onClose={() => setShowAnimation(false)}
                sx={{ zIndex: 1400 }}
                PaperProps={{
                    sx: {
                        borderTopLeftRadius: '10px',
                        borderTopRightRadius: '10px',
                        maxHeight: '55vh',
                        minHeight: '30vh',
                        backgroundColor: 'white',
                    }
                }}
            >
                {/* <Box sx={{ p: 0.75, pb: isNativeApp ? 3 : 0.75 }}> */}
                <Box sx={{ p: 0.75, pb: 3 }}>

                    {/* Handle bar */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
                        <Box sx={{ width: 28, height: 3, bgcolor: '#e0e0e0', borderRadius: 2 }} />
                    </Box>

                    {lines.length === 0 ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 4,
                            px: 2,
                        }}>
                            <InfoOutlinedIcon sx={{ fontSize: 40, color: '#bdbdbd', mb: 1.5 }} />
                            <Typography sx={{
                                fontSize: '13px',
                                color: '#666',
                                textAlign: 'center',
                                lineHeight: 1.5,
                            }}>
                                {t('PlayBuilderBottomBar02')}
                            </Typography>
                        </Box>
                    ) : (
                        <PlayAnimationLinesOrder
                            lines={lines}
                            players={players}
                            onUpdateLines={onUpdateLines}
                        />
                    )}
                </Box>
            </Drawer>
        </>
    );
}

